import React, { useMemo } from 'react';
// import './EditorChildren.less'
import {
  NodeTypes,
  IPropertyChild,
  compareEditorChildren,
  categoryIsMappingTypeSelector,
  getKeyFromPath,
} from 'components/property-editors/utils/ProviderNodeUtils';
import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderNode';
import { EditorCategory } from './EditorCategory';
import { MappingTypeSelector } from 'components/property-editors/MappingTypeSelector';
import { Editor } from 'components/Editors/Editor';
import { SkeletonLoader } from 'loaders/SkeletonLoader';

interface ContextProviderNodeProps {
  nodes?: ContextProviderNode[];
  depth?: number;
  path?: string[];
  nodeIndex?: number;
  skipSort?: boolean;
  /** Pass to show a skeleton loader instead of contents */
  isLoading?: boolean;
  expectedHeight?: number;
  /** true if it's an embedded property grid */
  embedded?: boolean;
}

// should do a shallow compare of props and avoid unnecessary re-renders (like for loading states)
export const EditorChildren = React.memo(_EditorChildren);

/**
 * Ideally these would be tidied up as actual components,
 * but with function components and fragments, it's become a semantic distinction.
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
function _EditorChildren(props: ContextProviderNodeProps) {
  const { nodes, depth = 0, embedded, expectedHeight, isLoading, path = [], nodeIndex: wizardIndex, skipSort } = props;

  // useUpdateReason('_EditorChildren', props);

  if (!nodes || !nodes.length) {
    // we only really want loaders or "No Results" at or near the root, so evaluate path.length
    return path.length < 1 ? (
      <>
        {isLoading || path.length > 1 || embedded ? '' : 'No Results'}
        {isLoading && (
          <SkeletonLoader height={expectedHeight || 150} style={embedded ? undefined : { padding: '1rem' }} />
        )}
      </>
    ) : null;
  }

  // console.warn('EditorChildren normal', children);
  // Sorting should already have happened at the root level, but these are sorted additionally here
  // to avoid sorting nodes for categories the user never expands (... which may be a premature optimization)
  const sorted = skipSort ? nodes : nodes.sort(compareEditorChildren);

  if (
    wizardIndex !== null &&
    wizardIndex !== undefined &&
    wizardIndex < nodes.length &&
    sorted.some(child => child.Node.__type === NodeTypes.Category)
  ) {
    // if wizard index is provided, we need to show only one (most likely category) node at a time
    return <GetChildForType child={sorted[wizardIndex]} depth={depth} path={path} />;
  }

  return (
    <>
      {sorted.map(child => (
        <GetChildForType key={getKeyFromPath(path, child)} {...{ child, depth, path }} />
      ))}
    </>
  );
}

// TODO need a way to re-focus a child by path after it re-renders,
// e.g. an unknown mapping field changing to a Constant field.

interface IGetChildForTypeProps {
  child: ContextProviderNode;
  depth?: number;
  path: string[];
}

function GetChildForType({ child, depth = 0, path }: IGetChildForTypeProps) {
  const childNodeName = child.Node.Name;
  const childNodePath = useMemo(() => [...path, childNodeName], [childNodeName, path]);
  switch (child.Node.__type) {
    // categories
    case NodeTypes.Category:
      // selector necessary here, since there's nothing about a Mapping Selector
      // to differentiate it from a regular category, except its children.
      // which seems like an abuse of a data-model, at face value.
      const isMappingType = categoryIsMappingTypeSelector(child);
      return isMappingType ? (
        <MappingTypeSelector key={childNodeName + depth} child={child as IPropertyChild} path={childNodePath} />
      ) : (
        <EditorCategory child={child} key={getKeyFromPath(path, child)} depth={depth} path={path} />
      );
    default:
      return (
        <Editor path={childNodePath} key={getKeyFromPath(path, child)} child={child as IPropertyChild} depth={depth} />
      );
  }
}
